import { FrsBulkImportResult } from './frsTypes';

/** User-facing label for the MoSCoW priority field. */
export const MOSCOW_FIELD_LABEL = 'MoSCoW';

/**
 * Constants for MoSCoW prioritization
 */
export const MOSCOW_OPTIONS = [
  'Must have',
  'Should have',
  'Could have',
  'Will not have',
];

export const MOSCOW_COLORS: Record<string, { bg: string; text: string }> = {
  'Must have': { bg: '#E6F4EA', text: '#137333' },
  'Should have': { bg: '#FEF7E0', text: '#B06000' },
  'Could have': { bg: '#E8F0FE', text: '#1967D2' },
  'Will not have': { bg: '#FEE8E8', text: '#C5221F' },
  "Won't have": { bg: '#FEE8E8', text: '#C5221F' },
};

export const DEFAULT_MOSCOW_COLOR = { bg: '#F1F3F4', text: '#5F6368' };

export const formatMoscowLabel = (value?: string) => {
  if (!value) return '-';
  if (value === "Won't have") return 'Will not have';
  return value;
};

export const formatFrsFieldLabel = (key: string) =>
  key === 'moscow' ? MOSCOW_FIELD_LABEL : key;

/**
 * Constants for FRS Gate stages
 */
export const GATE_OPTIONS = [
  'Planned',
  'Documentation (HLD & Designs)',
  'Documentation (LLD & Verification)',
  'Development (In Progress)',
  'Development (Internal Quality Control)',
  'Development (Acceptance)',
  'Development (Completed)',
  'Documentation (Update and Finalisation)',
  'Finalised',
];

export const GATE_COLORS: Record<string, string> = {
  Planned: '#1BB4EB',
  'Documentation (HLD & Designs)': '#1967D2',
  'Documentation (LLD & Verification)': '#1967D2',
  'Development (In Progress)': '#E37400',
  'Development (Internal Quality Control)': '#E37400',
  'Development (Acceptance)': '#E37400',
  'Development (Completed)': '#E37400',
  'Documentation (Update and Finalisation)': '#1967D2',
  Finalised: '#137333',
};

export const DEFAULT_GATE_COLOR = '#5F6368';

/**
 * Utility to convert category values to string format
 */
export const toCategoryNumber = (value: unknown): string => {
  if (Array.isArray(value)) {
    return value.join('.');
  }
  return String(value ?? '');
};

/**
 * Utility to extract array data from various API response formats
 */
export const extractDataArray = <T>(response: any): T[] => {
  const data = response?.data;
  if (!data) return [];
  if (Array.isArray(data)) return data;
  return data.data || data.categories || data.frsHierarchy || [];
};

/**
 * Utility to extract object data from various API response formats
 */
export const extractDataObject = <T>(response: any): T | null => {
  const data = response?.data;
  if (!data) return null;
  if (Array.isArray(data)) return null;

  const nested = data.data;
  if (nested !== undefined && nested !== null && !Array.isArray(nested)) {
    return nested as T;
  }

  return data as T;
};

/**
 * Formats a date string to a consistent human-readable format
 */
export const formatDate = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toString().replace(/ \(.*\)/, '');
};

/**
 * Strips HTML tags and collapses whitespace
 */
export const formatHtmlToText = (html = '') =>
  html
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Gets initials from a full name (max 2 characters)
 */
export const getInitials = (name: string) =>
  name
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0]?.toUpperCase())
    .slice(0, 2)
    .join('');

export const parseFilenameFromContentDisposition = (
  header: string | undefined
): string | null => {
  if (!header) return null;
  const utf8Match = header.match(/filename\*=(?:UTF-8'')?([^;\s]+)/i);
  if (utf8Match) {
    const raw = utf8Match[1].replace(/['"]/g, '');
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }
  const asciiMatch = header.match(/filename\s*=\s*("?)([^";\n]+)\1/i);
  return asciiMatch ? asciiMatch[2].trim() : null;
};

export const triggerBrowserDownload = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/** react-dropzone v11 + attr-accept expect a string or array, not a MIME map object. */
export const FRS_BULK_ACCEPT = [
  '.xlsx',
  '.xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
];

export const isExcelFile = (file: File): boolean => {
  const name = file.name.toLowerCase();
  return name.endsWith('.xlsx') || name.endsWith('.xls');
};

export const extractBulkImportResult = (response: any): FrsBulkImportResult => {
  const data = response?.data?.data ?? response?.data ?? response ?? {};
  const errors = Array.isArray(data?.errors) ? data.errors : [];

  return {
    categoriesCreated: Number(data?.categoriesCreated) || 0,
    categoriesUpdated: Number(data?.categoriesUpdated) || 0,
    frsCreated: Number(data?.frsCreated) || 0,
    frsUpdated: Number(data?.frsUpdated) || 0,
    errors: errors.map((item: any) => ({
      rowNumber: Number(item?.rowNumber) || 0,
      message: String(item?.message || 'Unknown error'),
    })),
  };
};

export const hasBulkImportChanges = (result: FrsBulkImportResult) =>
  result.categoriesCreated > 0 ||
  result.categoriesUpdated > 0 ||
  result.frsCreated > 0 ||
  result.frsUpdated > 0;

export const validateExcelFile = (file: File) => {
  if (isExcelFile(file)) {
    return null;
  }
  return {
    code: 'file-invalid-type',
    message: 'Only Excel files (.xlsx, .xls) are allowed',
  } as const;
};

export const readBlobErrorMessage = async (blob: Blob): Promise<string> => {
  try {
    const text = await blob.text();
    try {
      const json = JSON.parse(text);
      return json.message || text || 'Request failed';
    } catch {
      return text || 'Request failed';
    }
  } catch {
    return 'Request failed';
  }
};
